import { KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { CountryOptions, CountrySearch } from "../../components/Form/PhoneNumberInput/styles";
import { BANKS } from "../../model/banks";
import { Dropdown, Field, Option } from "../Editor/pages/Pricing/components/CurrencySelect/styles";

const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

type Props = {
  id: string;
  label: string;
  /** The bank's COMPE code; "" for none chosen yet */
  value: string;
  onChange: (code: string) => void;
  /** The field has an error under it: said to screen readers too. */
  invalid?: boolean;
};

/**
 * The bank of the account: a field that opens the list of banks with a search (name or code), like the editor's
 * currency field. The arrows move, Enter picks, Escape closes.
 */
export const BankSelect = ({ id, label, value, onChange, invalid }: Props) => {
  const { t } = useTranslation();
  const listId = useId();
  const container = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [isOpen, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const selected = BANKS.find(bank => bank.code === value);
  const filtered = useMemo(() => {
    const search = normalize(query.trim());
    return search ? BANKS.filter(({ code, name }) => normalize(name).includes(search) || code.includes(search)) : BANKS;
  }, [query]);

  // closes on a click outside it
  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  // keeps the highlighted bank in sight while moving with the arrows, not when the mouse moves it
  const scrollToActive = useRef(false);
  useEffect(() => {
    if (isOpen && scrollToActive.current) {
      list.current?.children[active]?.scrollIntoView?.({ block: "nearest" });
    }
    scrollToActive.current = false;
  }, [active, isOpen]);

  const open = () => {
    setQuery("");
    scrollToActive.current = true;
    setActive(Math.max(0, selected ? BANKS.indexOf(selected) : 0));
    setOpen(true);
  };

  // closed by the keyboard, the focus goes back to the field
  const close = () => {
    setOpen(false);
    field.current?.focus();
  };

  const pick = (code: string) => {
    onChange(code);
    close();
  };

  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      scrollToActive.current = true;
      setActive(Math.min(active + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      scrollToActive.current = true;
      setActive(Math.max(active - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (filtered[active]) pick(filtered[active].code);
    } else if (event.key === "Escape") {
      // the modal around it stays open: only the list closes
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <BankField ref={container}>
      <button
        ref={field}
        id={id}
        type="button"
        className="value"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={invalid || undefined}
        aria-label={selected ? `${label}: ${selected.name}` : label}
        onClick={() => (isOpen ? setOpen(false) : open())}>
        {selected ? (
          <>
            <span className="symbol">{selected.code}</span>
            <span className="name">{selected.name}</span>
          </>
        ) : (
          <span className="name placeholder">{t("pages.charges.bankSelect.choose")}</span>
        )}
      </button>
      {isOpen && (
        <Dropdown>
          <CountrySearch
            type="text"
            role="combobox"
            aria-expanded
            aria-autocomplete="list"
            aria-activedescendant={filtered[active] ? `${listId}-${filtered[active].code}` : undefined}
            autoFocus
            aria-label={t("pages.charges.bankSelect.search")}
            aria-controls={listId}
            placeholder={t("pages.charges.bankSelect.search")}
            value={query}
            onChange={event => {
              setQuery(event.target.value);
              scrollToActive.current = true;
              setActive(0);
            }}
            onKeyDown={keyDown}
          />
          <CountryOptions>
            <ul id={listId} ref={list} role="listbox" aria-label={label}>
              {filtered.length ? (
                filtered.map(({ code, name }, index) => (
                  <Option
                    key={code}
                    id={`${listId}-${code}`}
                    role="option"
                    aria-selected={code === value}
                    $active={index === active}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => pick(code)}>
                    <span className="symbol">{code}</span>
                    <span className="name">{name}</span>
                  </Option>
                ))
              ) : (
                <li className="empty">{t("pages.charges.bankSelect.none")}</li>
              )}
            </ul>
          </CountryOptions>
        </Dropdown>
      )}
    </BankField>
  );
};

/* While no bank is chosen: its gray hint fills the room of the code and the name */
const BankField = styled(Field)`
  .placeholder {
    grid-column: 1 / 3;
    font-weight: 400;
    color: var(--color-neutral-150);
  }
`;
